import { logException } from "@wss/error-tracking";
import { ThrowableStatus } from "@wss/error-tracking/throwable";
import {
  Item,
  CalculateShippingData,
  ShoppingCart,
  DiscountCodeResponse,
  ShippingAddress,
  CartRecommendedItemsResult,
  CartRecommendationResult,
  UpdateCartResponse,
} from "./Models";
import { calculateItemQuantity } from "./itemFunctions";
import { CarouselItem } from "~/ProductDetails/Carousel/CarouselItem";
import ItemData from "~/home/Carousel/ItemData";

type AddToCartItem = {
  itemNumber: string;
  quantity: string;
  accessories?: string;
};

export const maxItemCount = 30000;

export const deleteItemFromSavedList = async (
  selectedItem: Item,
  currentPage: number,
) => {
  const deleteSavedForLaterURL = "/shoppingcart:cart/deletesavedforlateritem/";
  const deletedItemData = new URLSearchParams();
  deletedItemData.append("wishlistItemId", String(selectedItem.wishlistItemId));
  deletedItemData.append("resultsPage", String(currentPage));
  try {
    const response = await fetch(deleteSavedForLaterURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: deletedItemData,
    });
    return response;
  } catch (err) {
    logException(err);
    return new Error("Delete item from saved list failed");
  }
};

export const deleteItemFromCart = async (
  selectedItem: Item,
  createFullResponse = true,
): Promise<ThrowableStatus<ShoppingCart>> => {
  const deleteCartItemURL = "/shoppingcart:cart/deletecartitem/";
  const deletedItemData = new URLSearchParams();
  deletedItemData.append("itemNumber", selectedItem.itemNumber.trim());
  deletedItemData.append("num", selectedItem.num.trim());
  deletedItemData.append("cartGroupType", selectedItem.cartGroupType);
  deletedItemData.append("quoteNumber", selectedItem.quoteNumber);
  deletedItemData.append("createFullResponse", createFullResponse.toString());
  try {
    const response = await fetch(deleteCartItemURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: deletedItemData,
    });

    if (!response.ok) {
      return {
        isError: true,
        status: response.status,
        error: new Error("Delete item failed.", { cause: response.statusText }),
      };
    }
    const data: ShoppingCart = await response.json();
    return { isError: false, value: data };
  } catch (error) {
    return {
      isError: true,
      status: null,
      error: new Error("Delete item from cart failed", { cause: error }),
    };
  }
};

export const addItemsToCart = async (items: AddToCartItem[]) => {
  const addCartURL = "/shoppingcart:cart/addcartitems/";
  const itemsToAddData = new URLSearchParams();

  itemsToAddData.append("itemsToAdd", JSON.stringify(items));
  try {
    const response = await fetch(addCartURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: itemsToAddData,
    });
    const data = await response.json<ShoppingCart>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Add items to cart failed");
  }
};

export const moveItemFromCartToSaved = async (
  selectedItem: Item,
  currentPage: number,
): Promise<ThrowableStatus<string[]>> => {
  const moveCartItemURL = "/shoppingcart:cart/movecartitemtosavedforlater/";
  const moveItem = new URLSearchParams();

  moveItem.append("itemNumber", selectedItem.itemNumber.trim());
  moveItem.append("quantity", String(calculateItemQuantity(selectedItem)));
  moveItem.append("resultsPage", String(currentPage));
  try {
    const res = await fetch(moveCartItemURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: moveItem,
    });
    if (!res.ok) {
      return {
        isError: true,
        status: res.status,
        error: new Error(`Move item from cart to saved fetch failed`, {
          cause: res.statusText,
        }),
      };
    }
    const data: { itemSortOrder: string[] } = await res.json();
    return { isError: false, value: data.itemSortOrder };
  } catch (error) {
    return {
      isError: true,
      status: null,
      error: new Error("Move Item Failed", { cause: error }),
    };
  }
};

export const deleteAllSavedForLaterItems = async () => {
  try {
    const response = await fetch(
      "/shoppingcart:cart/deleteallsavedforlateritems/",
      {
        method: "POST",
      },
    );
    return response;
  } catch (err) {
    logException(err);
    return new Error("Delete all saved for later items failed");
  }
};

export const emptyCartRequest = async () => {
  try {
    const response = await fetch("/shoppingcart:cart/emptycart/", {
      method: "POST",
    });
    const data = await response.json<ShoppingCart>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Error when emptying cart.");
  }
};

export const updateQuantityRequest = async (
  selectedItem: Item,
  value: string,
) => {
  const url = "/shoppingcart:cart/updatecart/";
  const currentItems = new URLSearchParams();
  const cartItem = {
    cartItemId: selectedItem.id,
    num: selectedItem.num,
    quantity: Number(value),
    cartGroupType: selectedItem.cartGroupType,
    quoteNumber: selectedItem.quoteNumber,
    itemNumber: selectedItem.itemNumberUrlEncoded,
  };

  currentItems.append("cartItems", JSON.stringify([cartItem]));
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: currentItems,
    });
    if (response.status >= 400) {
      return new Error("Update item quantity failed.");
    }
    const data = await response.json<ShoppingCart>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Update item quantity failed");
  }
};

export const getCurrentCart = async (
  selectedAddress: ShippingAddress | undefined,
) => {
  const url = "/ajax/cart";
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        selectedAddress,
      }),
    });
    if (response.status >= 400) {
      return new Error("Get current cart failed.");
    }
    const data = await response.json<ShoppingCart>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Get current cart failed");
  }
};

interface RefreshedCartParams {
  shippingAddress: {
    id: number;
    zip: string;
    state: string;
    stateid: string;
    country: string;
    isCommercial: boolean;
  };
  isTaxExempt: string;
}

export const getRefreshedCartData = async ({
  shippingAddress,
  isTaxExempt,
}: RefreshedCartParams) => {
  const refreshedCartDataUrl = "/shoppingcart:checkout/getrefreshedcartdata/";
  const refreshedCartData = new URLSearchParams();

  refreshedCartData.append("shippingAddress", JSON.stringify(shippingAddress));
  refreshedCartData.append("isTaxExempt", isTaxExempt);

  try {
    const response = await fetch(refreshedCartDataUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: refreshedCartData,
    });
    if (response.status >= 400) {
      return new Error("Refresh cart data fetch failed");
    }
    return response;
  } catch (err) {
    logException(err);
    return new Error("Refresh cart data fetch failed");
  }
};

export const calculateShippingRequest = async ({
  cartId,
  clientKey,
  userIndex,
  verifyId,
  shipToIndex,
  isDefaultAddress,
  referrer,
  zipCode,
  addressType,
  city,
  country,
  commonCarrier,
  itemCount,
  hasDivertCodes,
  createCheckoutContextCookies,
}: CalculateShippingData) => {
  if (itemCount > maxItemCount && !hasDivertCodes) {
    return new Error("Too many items");
  }

  const calculateShippingUrl = "/ajax/calculateshippingsf";
  const calculateShippingData = new URLSearchParams();

  calculateShippingData.append("blnCheckGoogle", "true");
  calculateShippingData.append("CartID", cartId);
  calculateShippingData.append("Client_Key", clientKey);
  calculateShippingData.append("User_Index", userIndex || "0");
  if (verifyId) {
    calculateShippingData.append("verifyId", verifyId);
  }
  if (shipToIndex) {
    calculateShippingData.append("shipTo_Index", shipToIndex.toString());
  }
  if (zipCode) {
    calculateShippingData.append("ZipCode", zipCode);
  }
  if (addressType) {
    calculateShippingData.append("AddressType", addressType);
  }
  if (isDefaultAddress) {
    calculateShippingData.append("isDefaultAddress", isDefaultAddress);
  }
  if (city) {
    calculateShippingData.append("City", city);
  }
  if (country) {
    calculateShippingData.append("Country", country);
  }

  if (commonCarrier) {
    calculateShippingData.append("commonCarrier", commonCarrier);
  }

  calculateShippingData.append("referrer", referrer);
  calculateShippingData.append("includePlusDiscountWithRegularQuote", "true");
  calculateShippingData.append(
    "createCheckoutContextCookies",
    createCheckoutContextCookies.toString(),
  );

  try {
    const response = await fetch(calculateShippingUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      credentials: "include",
      body: calculateShippingData,
    });

    if (response.status === 400) {
      return new Error("Invalid address");
    }

    if (response.status > 400) {
      return new Error("Refresh cart data fetch failed");
    }
    return response;
  } catch (err) {
    logException(err);
    return new Error("Refresh cart data fetch failed");
  }
};

export const fetchQuoteDiscountCode = async () => {
  try {
    const response = await fetch("/shoppingcart:cart/applyquotediscountcode/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (response.status >= 400) {
      return new Error("Discount code failed");
    }
    const data = await response.json<DiscountCodeResponse>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Discount code failed");
  }
};

export const fetchDiscountCode = async (
  code: string,
  employeeWarehouseNumber?: string,
) => {
  const codeItem = new URLSearchParams();
  codeItem.append("discountCode", code.trim());
  codeItem.append("orderNumber", "0");

  if (employeeWarehouseNumber) {
    codeItem.append("e_n", employeeWarehouseNumber);
  }

  try {
    const response = await fetch("/shoppingcart:cart/applydiscountcode/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: codeItem,
    });

    if (response.status >= 400) {
      return new Error("Discount code failed");
    }
    const data = await response.json<DiscountCodeResponse>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Discount code failed");
  }
};

export const deleteDiscountCode = async (code: string) => {
  const codeItem = new URLSearchParams();
  codeItem.append("discountCode", code);

  try {
    const response = await fetch("/shoppingcart:cart/deletediscountcode/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: codeItem,
    });

    if (response.status >= 400) {
      return new Error("Discount delete code failed");
    }
    const data = await response.json<ShoppingCart>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Discount delete code failed");
  }
};

interface CalculateUniqueShippingArgs {
  cartId: string;
  clientKey: string;
  userIndex?: string;
  email: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  addressType: string;
  liftGate: boolean;
  callInfo: boolean;
  referenceCode: string;
}

export const calculateUniqueShippingRequest = async ({
  email,
  address,
  city,
  province,
  postalCode,
  addressType,
  liftGate,
  callInfo,
  cartId,
  clientKey,
  userIndex,
  referenceCode,
}: CalculateUniqueShippingArgs) => {
  const shippingData = new URLSearchParams();
  shippingData.append("CartID", cartId);
  shippingData.append("Client_Key", clientKey);
  shippingData.append("User_Index", userIndex || "");
  shippingData.append("Email", email);
  shippingData.append("addy", `${address} ${city} ${province} ${postalCode}`);
  shippingData.append("cartbody", JSON.stringify({}));
  shippingData.append("liftgate", liftGate ? "1" : "0");
  shippingData.append("CallBeforeDelivery", callInfo ? "1" : "0");
  shippingData.append("adtype", addressType);
  shippingData.append("cart_reference_code", referenceCode);

  try {
    const response = await fetch("/ajax/sendccemail", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: shippingData,
    });
    return response;
  } catch (err) {
    logException(err);
    return new Error("Send email failed");
  }
};
export const transferFromPendingToCart = async () => {
  try {
    const response = await fetch(
      "/shoppingcart:cart/returntocartfrompendingorder/",
      {
        method: "POST",
      },
    );

    const data = await response.json<UpdateCartResponse>();
    return data;
  } catch (err) {
    logException(err);
    return new Error("Pending order failed");
  }
};

export const saveAutoReorderFrequency = async (
  itemId: string,
  intervalId: string,
) => {
  try {
    const response = await fetch(
      `/myaccount:autoreorder/savestagingdata/?itemNumber=${itemId}&intervalId=${intervalId}`,
      {
        method: "GET",
      },
    );
    return response;
  } catch (err) {
    logException(err);
    return new Error("Error updating autoreorder frequency");
  }
};

export const deleteAutoReorderItem = async (itemId: string) => {
  try {
    const response = await fetch(
      `/myaccount:autoreorder/deletestagingdata/?itemNumber=${itemId}`,
      {
        method: "GET",
      },
    );

    return response;
  } catch (err) {
    logException(err);
    return new Error("Error updating autoreorder status");
  }
};

const mapRecAiItems = (recAiCarouselItems: CarouselItem[]): ItemData[] => {
  if (!recAiCarouselItems.length) {
    return [];
  }

  return recAiCarouselItems.map((item) => ({
    itemNumber: item.itemNumber,
    description: item.description,
    price: item.price.toString(),
    link: item.link,
    feed_identifier: item.feedIdentifier,
    feedIdentifier: item.feedIdentifier,
    isWebstaurantPlusEligible: item.isWebstaurantPlusEligible,
    alt: item.altText,
    thumbnail: item.imagePath,
    smallImage: item.imagePath,
    rating: item.starRating,
    accessories: [],
    // need to figure out what to do with these props:
    product: {
      itemNumber: item.itemNumber,
      description: item.description,
      descriptionHtml: item.description,
      feedIdentifier: item.feedIdentifier,
      price: {
        price: item.price,
        salePrice: 0,
        displaySalePrice: false,
        hasQuantityDiscounts: false,
        hasLotQuantityDiscounts: false,
        hasSingleLotQuantityDiscount: false,
        displayMinimumAdvertisedPrice: false,
        applyTypeLPriceToPlatinumPlus: false,
        quantityDiscounts: [],
        minimumAdvertisedPriceProperties: {
          price: item.price,
          type: "",
        },
        minimumAdvertisedPriceOverride: {
          priceLevel5: item.price,
        },
      },
      isPriceOverride: false,
      accessories: [],
      allowOutOfStockPurchase: false,
      altHtml: "",
      badgeDisplayText: "",
      badgeImagePath: "",
      brand: "",
      brandLink: "",
      brandLogo: "",
      customizationUrl: "",
      link: item.link,
      primaryImagePath: "",
      originalId: "",
      shippingType: "",
      unitOfMeasure: "",
      customizationRequired: false,
      grouping: item.grouping,
      hasAccessories: item.hasAccessories,
      hasAvailableAccessories: false,
      hasBadge: false,
      hasFreeShipping: false,
      isAutomaticReorderEligible: false,
      isCustomizable: false,
      isEquivalentPart: false,
      isInStock: true,
      isNewProduct: false,
      isWebstaurantPlusEligible: false,
      mustPurchaseMoreThanOne: false,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers: false,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: false,
      minimumBuy: item.minimumBuy,
      mustBuy: item.mustBuy,
      startBuy: item.minimumBuy,
      maximumCustomizationPrice: 0,
      minimumCustomizationPrice: 0,
      weightInPounds: 0,
      oneToFiveStarRating: 0,
      overallRating: 0,
      documents: [],
      productSpecifications: [],
      customerExclusivity: {
        disallowNonMemberToPurchase: false,
        allowPlatinumToPurchase: true,
        allowWebstaurantPlusToPurchase: true,
      },
      groupingAttributes: {
        virtualGroupItemNumber: "",
        isVirtual: false,
        groupMembers: [],
        configurationOptionSummary: [],
      },
      outletAttributes: {
        isOutlet: false,
        warehouseLocation: 0,
        outletHighlights: "",
        originalPrice: 0,
        condition: "",
        outletProductId: 0,
      },
    },
    group: "",
    output_vendor: "",
    outputVendor: "",
    suffixes: [],
    uom: item.unitOfMeasure,
    web_category: "",
    webCategory: "",
  }));
};

export const fetchRecommendedItemsForCart =
  async (): Promise<CartRecommendedItemsResult> => {
    try {
      const response = await fetch("/new/api/cart/getrecommendeditems", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status >= 400) {
        throw new Error("Cart recommended items fetch failed");
      }

      const cartRecommendationResult =
        await response.json<CartRecommendationResult>();

      const mappedItems = cartRecommendationResult.fromCartRecommendations
        .length
        ? mapRecAiItems(cartRecommendationResult.fromCartRecommendations)
        : [];

      return {
        fromCartRecommendations: mappedItems,
        emptyCartRecommendations:
          cartRecommendationResult.emptyCartRecommendations,
        placementId: cartRecommendationResult.placementId,
        abTestIdentifier: cartRecommendationResult.abTestIdentifier,
      };
    } catch (err) {
      return {
        fromCartRecommendations: [],
        emptyCartRecommendations: [],
        placementId: "",
        error: new Error("Cart recommended items fetch failed"),
        abTestIdentifier: "",
      };
    }
  };
